import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import ApprovalInterface from './ApprovalInterface';
import DoctorGroupedApprovalMatrix from './DoctorGroupedApprovalMatrix';
import PermissionTimeManager from './PermissionTimeManager';
import PatientAccessSettings from './PatientAccessSettings';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { 
  Shield, 
  UserCheck, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Settings,
  Eye,
  Edit,
  Download,
  Share,
  Trash2,
  Calendar,
  User,
  FileText
} from 'lucide-react';
import { useAccessControl } from '../hooks/useAccessControl';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDataSync } from '../hooks/useDataSync';

interface AccessControlManagerProps {
  className?: string;
}

const AccessControlManager: React.FC<AccessControlManagerProps> = ({ className }) => {
  const { user, userProfile } = useAuth();
  const {
    permissions,
    requests,
    loading,
    revokePermission,
    reviewAccessRequest,
    loadRequests
  } = useAccessControl();
  const { revalidateAll } = useDataSync();
  const [activeTab, setActiveTab] = React.useState('requests');

  // Carregar todas as solicitações quando a aba 'all' for selecionada
  React.useEffect(() => {
    if (activeTab === 'all' && loadRequests) {
      console.log('🔄 Carregando todas as solicitações para aba "todas"');
      loadRequests('all'); // Carregar todas as solicitações
    } else if (activeTab === 'requests' && loadRequests) {
      console.log('🔄 Carregando solicitações pendentes para aba "solicitações"');
      loadRequests('pending'); // Carregar apenas pendentes
    }
  }, [activeTab, loadRequests]);

  const getPermissionIcon = (type: string) => {
    switch (type) {
      case 'read': return <Eye className="h-4 w-4" />;
      case 'write': return <Edit className="h-4 w-4" />;
      case 'download': return <Download className="h-4 w-4" />;
      case 'share': return <Share className="h-4 w-4" />;
      case 'delete': return <Trash2 className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pendente</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Aprovado</Badge>;
      case 'denied':
        return <Badge className="bg-red-100 text-red-800">Negado</Badge>;
      case 'revoked':
        return <Badge className="bg-gray-100 text-gray-800">Revogado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getPermissionTypeLabel = (type: string) => {
    switch (type) {
      case 'read': return 'Visualizar';
      case 'write': return 'Editar';
      case 'download': return 'Baixar';
      case 'share': return 'Compartilhar';
      case 'delete': return 'Excluir';
      default: return type;
    }
  };

  const getPermissionColor = (type: string) => {
    switch (type) {
      case 'read': return 'bg-blue-100 text-blue-800';
      case 'write': return 'bg-green-100 text-green-800';
      case 'download': return 'bg-purple-100 text-purple-800';
      case 'share': return 'bg-orange-100 text-orange-800';
      case 'delete': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDataTypeLabel = (type: string) => {
    switch (type) {
      case 'medical_records': return 'Registros Médicos';
      case 'documents': return 'Documentos';
      case 'lab_results': return 'Resultados de Exames';
      case 'prescriptions': return 'Prescrições';
      case 'imaging': return 'Imagens Médicas';
      case 'all': return 'Todos os Dados';
      default: return type || 'Não especificado';
    }
  };

  const getDataTypeIcon = (type: string) => {
    switch (type) {
      case 'medical_records': return <FileText className="h-3 w-3" />;
      case 'documents': return <FileText className="h-3 w-3" />;
      case 'lab_results': return <User className="h-3 w-3" />;
      case 'prescriptions': return <FileText className="h-3 w-3" />;
      case 'imaging': return <Eye className="h-3 w-3" />;
      case 'all': return <User className="h-3 w-3" />;
      default: return <FileText className="h-3 w-3" />;
    }
  };

  const getDataTypeColor = (type: string) => {
    switch (type) {
      case 'all': return 'bg-gray-100 text-gray-800';
      case 'medical_records': return 'bg-blue-100 text-blue-800';
      case 'documents': return 'bg-green-100 text-green-800';
      case 'lab_results': return 'bg-purple-100 text-purple-800';
      case 'prescriptions': return 'bg-orange-100 text-orange-800';
      case 'imaging': return 'bg-pink-100 text-pink-800';
      case 'personal_info': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    const result = await reviewAccessRequest(requestId, 'approved');
    if (result.success) {
      toast.success('Solicitação aprovada com sucesso!');
    } else {
      toast.error(result.error || 'Erro ao aprovar solicitação');
    }
  };

  const handleDenyRequest = async (requestId: string) => {
    const result = await reviewAccessRequest(requestId, 'denied');
    if (result.success) {
      toast.success('Solicitação negada com sucesso!');
    } else {
      toast.error(result.error || 'Erro ao negar solicitação');
    }
  };

  const handleRevokePermission = async (permissionId: string) => {
    const result = await revokePermission(permissionId);
    if (result.success) {
      toast.success('Permissão revogada com sucesso!');
    } else {
      toast.error(result.error || 'Erro ao revogar permissão');
    }
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false; // Permissões sem data de expiração (never) nunca expiram
    return new Date(expiresAt) < new Date();
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const activePermissions = permissions.filter(p => 
    p.status === 'approved' && !isExpired(p.expires_at)
  );

  if (userProfile?.role !== 'patient') {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          O controle de acesso está disponível apenas para pacientes.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Controle de Acesso</h2>
          <p className="text-gray-600">Gerencie quem pode acessar seus dados médicos</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="requests" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Solicitações ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="manage" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Ativas ({activePermissions.length})
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Configurações
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Todas ({permissions.length + requests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-4">
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <strong>{pendingRequests.length}</strong> solicitação{pendingRequests.length !== 1 ? 'ões' : ''} aguardando sua revisão
              {pendingRequests.length > 0 && (
                <span className="ml-2 text-blue-600">
                  • Agrupadas por médico para facilitar a aprovação
                </span>
              )}
            </div>
            
            <DoctorGroupedApprovalMatrix
              requests={pendingRequests as any}
              onRequestProcessed={() => {
                // Recarregar dados sem refresh da página
                console.log('✅ Solicitação processada - revalidando dados...');
                
                // Usar revalidação automática via useDataSync
                revalidateAll();
              }}
            />
          </div>
        </TabsContent>



        <TabsContent value="manage" className="space-y-4">
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <strong>{activePermissions.length}</strong> permissão{activePermissions.length !== 1 ? 'ões' : ''} ativa{activePermissions.length !== 1 ? 's' : ''}
              {activePermissions.length > 0 && (
                <span className="ml-2 text-green-600">
                  • Agrupadas por médico responsável
                </span>
              )}
            </div>
            
            {activePermissions.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <CheckCircle className="h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500 text-center">
                    Nenhuma permissão ativa encontrada
                  </p>
                </CardContent>
              </Card>
            ) : (
              (() => {
                // Agrupar permissões ativas por médico
                const groupedByDoctor = activePermissions.reduce((acc, permission) => {
                  const doctorId = (permission as any).requester?.id || 'unknown';
        const doctorName = (permission as any).requester?.full_name || 'Médico Desconhecido';
        const doctorEmail = (permission as any).requester?.email || '';
        
        if (!acc[doctorId]) {
          acc[doctorId] = {
            doctor: { id: doctorId, full_name: doctorName, email: doctorEmail },
            permissions: []
          };
        }
        acc[doctorId].permissions.push(permission);
                  return acc;
                }, {} as Record<string, { doctor: any; permissions: any[] }>);

                return Object.entries(groupedByDoctor).map(([doctorId, group]) => (
                  <Card key={doctorId} className="border-green-200 bg-green-50">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-green-100">
                            <UserCheck className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <CardTitle className="text-lg text-green-800">
                              {group.doctor.full_name}
                            </CardTitle>
                            <CardDescription className="text-green-600">
                              {group.doctor.email} • {group.permissions.length} permissão{group.permissions.length !== 1 ? 'ões' : ''} ativa{group.permissions.length !== 1 ? 's' : ''}
                            </CardDescription>
                          </div>
                        </div>
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Ativo
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-3">
                      {group.permissions.map((permission) => (
                        <div key={permission.id} className="bg-white rounded-lg p-4 border border-green-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Badge className={getPermissionColor(permission.permission_type)}>
                                {getPermissionIcon(permission.permission_type)}
                                <span className="ml-1">{getPermissionTypeLabel(permission.permission_type)}</span>
                              </Badge>
                              <Badge className={getDataTypeColor((permission as any).data_type || 'all')}>
                                {getDataTypeIcon((permission as any).data_type || 'all')}
                                <span className="ml-1">{getDataTypeLabel((permission as any).data_type || 'all')}</span>
                              </Badge>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRevokePermission(permission.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Revogar
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Concedido:</span><br />
                              <span className="font-medium">
                                {permission.granted_at ? formatDistanceToNow(new Date(permission.granted_at), { 
                                  addSuffix: true, 
                                  locale: ptBR 
                                }) : 'N/A'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Expira:</span><br />
                              <span className="font-medium">
                                {permission.expires_at ? (
                                  isExpired(permission.expires_at) ? (
                                    <span className="text-red-600">Expirado</span>
                                  ) : (
                                    formatDistanceToNow(new Date(permission.expires_at), { 
                                      addSuffix: true, 
                                      locale: ptBR 
                                    })
                                  )
                                ) : (
                                  <span className="text-green-600">Nunca</span>
                                )}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Status:</span><br />
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Ativa
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ));
              })()
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <PatientAccessSettings />
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <strong>{permissions.length + requests.length}</strong> item{(permissions.length + requests.length) !== 1 ? 's' : ''} no histórico
              {(permissions.length + requests.length) > 0 && (
                <span className="ml-2 text-blue-600">
                  • Agrupados por médico para melhor organização
                </span>
              )}
            </div>
            
            {(permissions.length === 0 && requests.length === 0) ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Shield className="h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500 text-center">
                    Nenhuma permissão ou solicitação encontrada
                  </p>
                </CardContent>
              </Card>
            ) : (
              (() => {
                // Combinar todas as solicitações e permissões
                const allItems = [
                  ...requests.map(r => ({ ...r, type: 'request', doctor: (r as any).requester })),
                  ...permissions.map(p => ({ ...p, type: 'permission' }))
                ];
                
                // Agrupar por médico
                 const groupedByDoctor = allItems.reduce((acc, item) => {
                   const doctor = (item as any).doctor;
                   const doctorId = doctor?.id || 'unknown';
        const doctorName = doctor?.full_name || 'Médico Desconhecido';
        const doctorEmail = doctor?.email || '';
        
        if (!acc[doctorId]) {
          acc[doctorId] = {
            doctor: { id: doctorId, full_name: doctorName, email: doctorEmail },
            items: []
          };
        }
        acc[doctorId].items.push(item);
                  return acc;
                }, {} as Record<string, { doctor: any; items: any[] }>);

                return Object.entries(groupedByDoctor).map(([doctorId, group]) => {
                  const pendingCount = group.items.filter(item => item.status === 'pending').length;
                  const activeCount = group.items.filter(item => item.status === 'approved' && item.type === 'permission' && !isExpired(item.expires_at)).length;
                  const totalCount = group.items.length;
                  
                  return (
                    <Card key={doctorId} className="border-blue-200 bg-blue-50">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100">
                              <User className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <CardTitle className="text-lg text-blue-800">
                                {group.doctor.full_name}
                              </CardTitle>
                              <CardDescription className="text-blue-600">
                                {group.doctor.email} • {totalCount} item{totalCount !== 1 ? 's' : ''} no histórico
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {pendingCount > 0 && (
                              <Badge className="bg-yellow-100 text-yellow-800">
                                <Clock className="h-3 w-3 mr-1" />
                                {pendingCount} pendente{pendingCount !== 1 ? 's' : ''}
                              </Badge>
                            )}
                            {activeCount > 0 && (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {activeCount} ativa{activeCount !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-3">
                        {group.items
                          .sort((a, b) => new Date(b.created_at || b.granted_at || '').getTime() - new Date(a.created_at || a.granted_at || '').getTime())
                          .map((item) => {
                            const isRequest = item.type === 'request';
                            const isEmergency = isRequest && (item.justification?.toLowerCase().includes('emergência') || item.justification?.toLowerCase().includes('urgente'));
                            
                            return (
                              <div key={item.id} className={`bg-white rounded-lg p-4 border ${
                                isRequest 
                                  ? (isEmergency ? 'border-red-200' : 'border-yellow-200')
                                  : item.status === 'approved' && !isExpired(item.expires_at)
                                  ? 'border-green-200'
                                  : 'border-gray-200'
                              }`}>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    {isRequest && isEmergency && (
                                      <Badge className="bg-red-100 text-red-800">
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Emergência
                                      </Badge>
                                    )}
                                    <Badge className={getPermissionColor(item.permission_type)}>
                                      {getPermissionIcon(item.permission_type)}
                                      <span className="ml-1">{getPermissionTypeLabel(item.permission_type)}</span>
                                    </Badge>
                                    <Badge className={getDataTypeColor(item.data_type || 'all')}>
                                      {getDataTypeIcon(item.data_type || 'all')}
                                      <span className="ml-1">{getDataTypeLabel(item.data_type || 'all')}</span>
                                    </Badge>
                                  </div>
                                  {getStatusBadge(item.status)}
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">{isRequest ? 'Solicitado' : 'Concedido'}:</span><br />
                                    <span className="font-medium">
                                      {formatDistanceToNow(new Date(item.created_at || item.granted_at || ''), { 
                                        addSuffix: true, 
                                        locale: ptBR 
                                      })}
                                    </span>
                                  </div>
                                  {!isRequest && (
                                    <div>
                                      <span className="text-gray-500">Expira:</span><br />
                                      <span className="font-medium">
                                        {item.expires_at ? (
                                          isExpired(item.expires_at) ? (
                                            <span className="text-red-600">Expirado</span>
                                          ) : (
                                            formatDistanceToNow(new Date(item.expires_at), { 
                                              addSuffix: true, 
                                              locale: ptBR 
                                            })
                                          )
                                        ) : (
                                          <span className="text-green-600">Nunca</span>
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-gray-500">Tipo:</span><br />
                                    <span className="font-medium">
                                      {isRequest ? 'Solicitação' : 'Permissão'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </CardContent>
                    </Card>
                  );
                });
              })()
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccessControlManager;